import '../../styles/components/admin.css'
import { requireAuth, signOut, getAllProducts, deleteProduct, updateProduct, getAccessToken } from '../../lib/supabase.js'
import { formatPrice } from '../../utils/helpers.js'

export function renderAdminProducts(outlet) {
  requireAuth(async () => {
    outlet.innerHTML = `
      <div class="admin-page">
        <div class="container">
          <div class="admin-topbar">
            <h1 class="admin-title">Products</h1>
            <div style="display:flex; gap: var(--space-3); align-items:center;">
              <a href="#/admin/blog" class="btn btn-sm">Blog</a>
              <a href="#/admin/categories" class="btn btn-sm">Categories</a>
              <a href="#/admin/promo-codes" class="btn btn-sm">Promo Codes</a>
              <a href="#/admin/users" class="btn btn-sm">Customers</a>
              <a href="#/admin/products/new" class="btn btn-primary btn-sm">+ Add Product</a>
              <button class="admin-signout" id="admin-signout-btn">Sign Out</button>
            </div>
          </div>

          <div id="admin-products-content">
            <div class="products-loading" style="padding: var(--space-16) 0; text-align:center;">
              <div class="spinner"></div>
              <span style="margin-left: var(--space-3); color: var(--color-text-secondary);">Loading products…</span>
            </div>
          </div>
        </div>
      </div>
    `

    document.getElementById('admin-signout-btn')?.addEventListener('click', async () => {
      await signOut()
      window.location.hash = '#/admin/login'
    })

    await loadProducts(outlet)
  })
}

let products = []

async function loadProducts(outlet) {
  const content = document.getElementById('admin-products-content')
  if (!content) return

  try {
    products = await getAllProducts()

    if (products.length === 0) {
      content.innerHTML = `
        <div class="admin-empty">
          <p>No products yet.</p>
          <a href="#/admin/products/new" class="btn btn-primary" style="margin-top: var(--space-4);">Add your first product</a>
        </div>
      `
      return
    }

    renderTable(content)

    content.addEventListener('click', async (e) => {
      const syncBtn = e.target.closest('[data-sync]')
      if (syncBtn) {
        const id = syncBtn.dataset.sync
        const name = syncBtn.dataset.name
        const originalLabel = syncBtn.textContent
        syncBtn.disabled = true
        syncBtn.textContent = 'Syncing…'
        try {
          await syncToStripe(id, 'upsert')
          // Update local cache so badge disappears without a reload.
          const idx = products.findIndex(p => p.id === id)
          if (idx >= 0 && !products[idx].stripe_product_id) {
            products[idx].stripe_product_id = 'pending'
          }
          renderTable(content)
        } catch (err) {
          alert(`Failed to sync "${name}": ${err.message || 'Unknown error'}`)
          syncBtn.disabled = false
          syncBtn.textContent = originalLabel
        }
        return
      }

      const deleteBtn = e.target.closest('[data-delete]')
      if (deleteBtn) {
        const id = deleteBtn.dataset.delete
        const name = deleteBtn.dataset.name
        if (!confirm(`Delete "${name}"? This cannot be undone.`)) return
        deleteBtn.disabled = true
        deleteBtn.textContent = 'Deleting…'
        try {
          // Best-effort: archive in Stripe before deleting from Supabase.
          await syncToStripe(id, 'archive').catch(err => {
            console.warn('[admin/products] Stripe archive failed (continuing with delete):', err.message)
          })
          await deleteProduct(id)
          products = products.filter(p => p.id !== id)
          renderTable(content)
        } catch (err) {
          alert('Failed to delete: ' + (err.message || 'Unknown error'))
          deleteBtn.disabled = false
          deleteBtn.textContent = 'Delete'
        }
        return
      }

      const moveBtn = e.target.closest('[data-move]')
      if (moveBtn) {
        const id = moveBtn.dataset.move
        const dir = moveBtn.dataset.dir
        const idx = products.findIndex(p => p.id === id)
        const swapIdx = dir === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= products.length) return

        const a = products[idx]
        const b = products[swapIdx]
        const tmpOrder = a.sort_order
        a.sort_order = b.sort_order
        b.sort_order = tmpOrder;
        [products[idx], products[swapIdx]] = [products[swapIdx], products[idx]]

        renderTable(content)

        try {
          await Promise.all([
            updateProduct(a.id, { sort_order: a.sort_order }),
            updateProduct(b.id, { sort_order: b.sort_order }),
          ])
        } catch (err) {
          alert('Failed to save order: ' + (err.message || 'Unknown error'))
        }
      }
    })
  } catch (err) {
    content.innerHTML = `<div class="admin-error">Failed to load products: ${err.message}</div>`
  }
}

function renderTable(content) {
  const tbody = document.getElementById('admin-products-tbody')
  if (tbody) {
    tbody.innerHTML = products.map((p, i) => renderRow(p, i, products.length)).join('')
    return
  }
  content.innerHTML = `
    <div class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th style="width:60px">Order</th>
            <th>Name</th>
            <th>SKU</th>
            <th>Price</th>
            <th>Stock</th>
            <th>Category</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody id="admin-products-tbody">
          ${products.map((p, i) => renderRow(p, i, products.length)).join('')}
        </tbody>
      </table>
    </div>
  `
}

function renderRow(p, i, total) {
  const stripeSynced = Boolean(p.stripe_product_id)
  return `
    <tr id="admin-row-${CSS.escape(p.id)}">
      <td>
        <div style="display:flex; flex-direction:column; gap:2px; align-items:center;">
          <button data-move="${p.id}" data-dir="up" ${i === 0 ? 'disabled' : ''}
            style="background:none; border:none; cursor:pointer; color:${i === 0 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'}; padding:2px 6px; font-size:12px; line-height:1;">▲</button>
          <button data-move="${p.id}" data-dir="down" ${i === total - 1 ? 'disabled' : ''}
            style="background:none; border:none; cursor:pointer; color:${i === total - 1 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'}; padding:2px 6px; font-size:12px; line-height:1;">▼</button>
        </div>
      </td>
      <td class="cell-name" title="${p.name}">${p.name}</td>
      <td class="cell-sku">${p.sku || '—'}</td>
      <td>${formatPrice(p.price_cents)}</td>
      <td>${p.stock_qty}</td>
      <td>${p.category}</td>
      <td>
        <span class="admin-badge ${p.active ? 'admin-badge-active' : 'admin-badge-inactive'}">
          ${p.active ? 'Active' : 'Inactive'}
        </span>
        ${stripeSynced ? '' : '<span class="admin-badge admin-badge-warning" style="margin-left:6px;" title="Not yet created in Stripe">Not in Stripe</span>'}
      </td>
      <td class="cell-actions">
        <a href="#/admin/products/${p.id}/edit" class="btn btn-sm" style="font-size: var(--text-xs);">Edit</a>
        <button
          class="btn btn-sm"
          style="font-size: var(--text-xs);"
          data-sync="${p.id}"
          data-name="${p.name.replace(/"/g, '&quot;')}"
        >${stripeSynced ? 'Re-sync' : 'Sync to Stripe'}</button>
        <button
          class="btn btn-sm"
          style="font-size: var(--text-xs); border-color: var(--color-error); color: var(--color-error);"
          data-delete="${p.id}"
          data-name="${p.name.replace(/"/g, '&quot;')}"
        >Delete</button>
      </td>
    </tr>
  `
}

async function syncToStripe(productId, action) {
  const token = await getAccessToken()
  if (!token) throw new Error('Not signed in')
  const res = await fetch('/api/admin/sync-stripe-product', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action, productId }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data?.error || `Sync failed (${res.status})`)
  return data
}
